import { PipelineSeriesByRegionColour, PipelineSeriesOnBuild } from '@nova/types';

import { ClientTextureUtils } from './cl-texture-utils';
import { IScaleParams } from './models/scale-params.interface';
import { IScalePipelineResult } from './models/scale-pipeline-result.interface';
import { applyColorToUnmaskedRegions } from './render-pipelines/pixel-processors';
import { StaticPipelineOperations } from './StaticPipelineOps';

// TODO: We can optimize this chain by having pipeline classes take Uint8Array, not necessarily ImageData
// They would still need to have accompanying width and height values
// And should still always accept ImageData for when they are used independently, not apart of the specific chain.

export class ClientTextureGeneration {
  public static CreateDirtLayeredTexture(img: HTMLImageElement, dirtTileImgData: ImageData, gl?: WebGLRenderingContext): HTMLImageElement {
    const partImgData = ClientTextureUtils.GetImageDataFromImage(img);
    const tiledDirtPixelData: Uint8Array = StaticPipelineOperations.GetTiled(dirtTileImgData, partImgData.width, partImgData.height, gl);
    const tiledDirtImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledDirtPixelData, partImgData.width, partImgData.height);
    const tiledDirtMaskedPixelData: Uint8Array = StaticPipelineOperations.GetMasked(tiledDirtImageData, partImgData, false, gl);
    const tiledDirtMaskedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledDirtMaskedPixelData, partImgData.width, partImgData.height);
    const dirtyPartPixelData: Uint8Array = StaticPipelineOperations.GetOverlayed(partImgData, tiledDirtMaskedImageData, gl);
    const dirtyPartImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(dirtyPartPixelData, partImgData.width, partImgData.height);
    return ClientTextureUtils.GetImageFromImageData(dirtyPartImageData);
  }

  public static CreateTiled(tileImgData: ImageData, width: number, height: number, gl?: WebGLRenderingContext): HTMLImageElement {
    const tiledPixelData: Uint8Array = StaticPipelineOperations.GetTiled(tileImgData, width, height, gl);
    const tiledImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledPixelData, width, height);
    return ClientTextureUtils.GetImageFromImageData(tiledImageData);
  }

  /**
   * If the imageToMask and imageAsMask are not the same dimensions, the 'imageAsMask' will be scaled to fit the 'imageToMask'.
   * If such scaling is required, be sure that the css width/height of the 'imageAsMask' is not modified,
   * or at least matches the intrinsic texture size, or the canvas created will be set to the set styling,
   * while the intrinsic size/pixel data will be used for processing.
   * @param imageToMask
   * @param imageAsMask
   * @returns
   */
  public static CreateMasked(imageToMask: HTMLImageElement, imageAsMask: HTMLImageElement, gl?: WebGLRenderingContext): HTMLImageElement {
    const imageToMaskData = ClientTextureUtils.GetImageDataFromImage(imageToMask);
    let imageAsMaskData = ClientTextureUtils.GetImageDataFromImage(imageAsMask);

    if (imageToMaskData.width !== imageAsMaskData.width || imageToMaskData.height !== imageAsMaskData.height) {
      console.warn(`ClientTextureGeneration::CreateMasked: Image dimensions do not match.` +
        `A new 'imageAsMask' mask texture will be created and scaled-to-fit 'imageToMask'.`);

      const scaleResult: IScalePipelineResult =
        StaticPipelineOperations.GetScaled(imageAsMaskData, { widthToPx: imageToMaskData.width, heightToPx: imageToMaskData.height }, gl);
      imageAsMaskData = ClientTextureUtils.GetImageDataFromPixels(scaleResult.pixelData, scaleResult.newWidthPx, scaleResult.newHeightPx);
    }

    const maskedPixelData: Uint8Array = StaticPipelineOperations.GetMasked(imageToMaskData, imageAsMaskData, false, gl);
    const maskedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(maskedPixelData, imageToMaskData.width, imageToMaskData.height);
    return ClientTextureUtils.GetImageFromImageData(maskedImageData);
  }

  public static CreateTinted(img: HTMLImageElement, colour: number[], gl?: WebGLRenderingContext): HTMLImageElement {
    const imgData = ClientTextureUtils.GetImageDataFromImage(img);
    const tintedPixelData: Uint8Array = StaticPipelineOperations.GetColourTinted(imgData, colour, gl);
    const tintedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tintedPixelData, imgData.width, imgData.height);
    return ClientTextureUtils.GetImageFromImageData(tintedImageData);
  }

  /**
   * Applies a colour tint to the full image, or only to pixels outside swatch mask regions when a mask is provided.
   */
  public static applyTintToUnmaskedRegions(
    baseImg: HTMLImageElement,
    colour: number[],
    maskImg?: HTMLImageElement,
    gl?: WebGLRenderingContext
  ): HTMLImageElement {
    if (!maskImg) {
      return ClientTextureGeneration.CreateTinted(baseImg, colour, gl);
    }

    const baseImgData = ClientTextureUtils.GetImageDataFromImage(baseImg);
    const maskImgData = ClientTextureUtils.GetImageDataFromImage(maskImg);
    const result = applyColorToUnmaskedRegions(baseImgData, maskImgData, colour);
    return ClientTextureUtils.GetImageFromImageData(result);
  }

  public static CreateScaled(img: HTMLImageElement, scale: IScaleParams, gl?: WebGLRenderingContext): HTMLImageElement {
    const imgData = ClientTextureUtils.GetImageDataFromImage(img);
    const scaleResult: IScalePipelineResult = StaticPipelineOperations.GetScaled(imgData, scale, gl);
    const scaledImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(scaleResult.pixelData, scaleResult.newWidthPx, scaleResult.newHeightPx);
    return ClientTextureUtils.GetImageFromImageData(scaledImageData);
  }

  /**
   * Creates an image from a pipeline series.
   * @param img - The base image.
   * @param seriesBuilt - The pipeline series.
   * @param gl - The WebGL rendering context.
   * @returns The created image.
   */
  public static CreateFromPipelineSeries(img: HTMLImageElement, seriesBuilt: PipelineSeriesOnBuild, gl?: WebGLRenderingContext): HTMLImageElement {
    if (seriesBuilt.length === 0) { return img; }

    const imgData = ClientTextureUtils.GetImageDataFromImage(img);
    const finalPixelData: Uint8Array = StaticPipelineOperations.RunPipelineSeriesAsPixels(imgData, seriesBuilt, gl);
    const finalImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(finalPixelData, imgData.width, imgData.height);
    return ClientTextureUtils.GetImageFromImageData(finalImageData);
  }

  /**
   * Creates an image from a pipeline series by region colour.
   * @param baseImg - The base image.
   * @param mask - The mask image.
   * @param seriesByRegionColour - The pipeline series by region colour.
   * @param gl - The WebGL rendering context.
   * @returns The created image.
   */
  public static CreateFromPipelineSeriesByRegionColour(
    baseImg: HTMLImageElement, mask: HTMLImageElement,
    seriesByRegionColour: PipelineSeriesByRegionColour,
    gl?: WebGLRenderingContext
  ): HTMLImageElement {
    if (Object.keys(seriesByRegionColour).length === 0) { return mask; }

    const baseImgData = ClientTextureUtils.GetImageDataFromImage(baseImg);
    const maskImgData = ClientTextureUtils.GetImageDataFromImage(mask);
    const finalPixelData: Uint8Array = StaticPipelineOperations.RunPipelineSeriesByRegionColourAsPixels(
      baseImgData,
      maskImgData,
      seriesByRegionColour,
      gl
    );
    const finalImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(finalPixelData, baseImgData.width, baseImgData.height);
    return ClientTextureUtils.GetImageFromImageData(finalImageData);
  }

  public static Cleanup() {
    ClientTextureUtils.DestroyCanvas();
  }
}
